import { Fragment, useState } from "react";

import { Menu, Transition } from "@headlessui/react";
import { ArrowTopRightOnSquareIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import usePartShopifyUrl from "../../data/usePartShopifyUrl";
import { IPart } from "../../models/Part";
import getLiveEbayItemNumber from "../../utils/getLiveEbayItemNumber";
import openInNewTab from "../../utils/openInNewTab";
import Tooltip from "./Tooltip";
import { ButtonInput } from "./Inputs/ButtonInput";

interface ViewOnButtonProps {
  part: IPart;
  compact?: boolean;
}

export default function ViewOnButton({ part, compact = false }: ViewOnButtonProps) {
  const { t } = useTranslation("common");
  const ebayItemNumber = getLiveEbayItemNumber(part);
  const hasEbay = Boolean(ebayItemNumber);
  const hasShopify = part.isPublishedOnShopify;
  const [fetchingShopify, setFetchingShopify] = useState(false);
  const { fetchShopifyUrl } = usePartShopifyUrl(part.id);

  if (!hasEbay && !hasShopify) return null;

  const openEbay = (e: React.MouseEvent) => {
    e.stopPropagation();
    openInNewTab(`https://www.ebay.co.uk/itm/${ebayItemNumber}`);
  };

  const openShopify = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setFetchingShopify(true);
    const url = await fetchShopifyUrl();
    setFetchingShopify(false);
    if (url) openInNewTab(url);
  };

  // Single platform — compact icon button
  if (compact) {
    if (hasEbay && !hasShopify) {
      return (
        <Tooltip content={t("View listing on eBay")}>
          <button
            type="button"
            onClick={openEbay}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary-600"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      );
    }

    if (!hasEbay && hasShopify) {
      return (
        <Tooltip content={t("View listing on Shopify")}>
          <button
            type="button"
            onClick={openShopify}
            disabled={fetchingShopify}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-emerald-600 disabled:opacity-50"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </Tooltip>
      );
    }

    // Both — compact dropdown
    return (
      <Menu as="div" className="relative">
        <Tooltip content={t("View listing")}>
          <Menu.Button
            onClick={(e) => e.stopPropagation()}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-primary-600"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
          </Menu.Button>
        </Tooltip>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-50 mt-1 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={openEbay}
                    className={`block w-full px-4 py-2 text-left text-sm ${active ? "bg-slate-50 text-slate-900" : "text-slate-700"}`}
                  >
                    {t("View on eBay")}
                  </button>
                )}
              </Menu.Item>
              <Menu.Item disabled={fetchingShopify}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={openShopify}
                    disabled={fetchingShopify}
                    className={`block w-full px-4 py-2 text-left text-sm ${active ? "bg-slate-50 text-slate-900" : "text-slate-700"} disabled:opacity-50`}
                  >
                    {fetchingShopify ? t("Loading…") : t("View on Shopify")}
                  </button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    );
  }

  // Full button — single platform direct open
  if (hasEbay && !hasShopify) {
    return (
      <ButtonInput label={t("View on eBay")} isSubmit={false} onClick={openEbay} classes={undefined} />
    );
  }

  if (!hasEbay && hasShopify) {
    return (
      <ButtonInput
        label={t("View on Shopify")}
        isSubmit={false}
        onClick={openShopify}
        disabled={fetchingShopify}
        isLoading={fetchingShopify}
        classes={undefined}
      />
    );
  }

  // Both — full dropdown button
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        as="div"
        className="inline-flex items-center gap-1 rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer select-none"
      >
        {t("View on")}
        <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-1 w-44 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={openEbay}
                  className={`block w-full px-4 py-2 text-left text-sm ${active ? "bg-slate-50 text-slate-900" : "text-slate-700"}`}
                >
                  eBay
                </button>
              )}
            </Menu.Item>
            <Menu.Item disabled={fetchingShopify}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={openShopify}
                  disabled={fetchingShopify}
                  className={`block w-full px-4 py-2 text-left text-sm ${active ? "bg-slate-50 text-slate-900" : "text-slate-700"} disabled:opacity-50`}
                >
                  {fetchingShopify ? t("Loading…") : "Shopify"}
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
